import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { endpoints, streamChat, type ChatMessage } from "../lib/api";

const STARTERS = ["What was decided?", "What are the action items?", "Sum it up in three bullet points."];

/** Questions about one meeting, answered from its minutes and transcript.
 * A button in the corner of the meeting page; the conversation is kept, per
 * person, so it's there when they come back. */
export function MeetingChat({ sessionId, csrfToken, ready }: {
  sessionId: string;
  csrfToken: string;
  /** Only once there's a transcript to answer from. */
  ready: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [large, setLarge] = useState(false);
  const [draft, setDraft] = useState("");
  // The exchange being written: the question, and the answer so far.
  const [pending, setPending] = useState<{ question: string; answer: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  const history = useQuery({
    queryKey: ["chat", sessionId],
    queryFn: () => endpoints.chatHistory(sessionId),
    enabled: open && ready,
  });
  const messages = history.data?.messages ?? [];

  const clear = useMutation({
    mutationFn: () => endpoints.clearChat(sessionId, csrfToken),
    onSuccess: () => {
      queryClient.setQueryData(["chat", sessionId], { messages: [] });
      setError(null);
    },
  });

  // Keep the newest words in view as they arrive.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [messages.length, pending?.answer, open]);

  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);

  async function send(question: string) {
    question = question.trim();
    if (!question || pending) return;
    setDraft("");
    setError(null);
    setPending({ question, answer: "" });
    let answer = "";
    try {
      await streamChat(sessionId, question, csrfToken, (text) => {
        answer += text;
        setPending({ question, answer });
      });
      // Saved server-side once complete; this shows it without a refetch.
      queryClient.setQueryData(["chat", sessionId], (old: { messages: ChatMessage[] } | undefined) => ({
        messages: [...(old?.messages ?? []), { role: "user", content: question }, { role: "assistant", content: answer }],
      }));
    } catch (e) {
      // Nothing was saved: give the question back to send again.
      setError((e as Error).message);
      setDraft(question);
    } finally {
      setPending(null);
    }
  }

  if (!open) {
    return (
      <button className="chat-fab" onClick={() => setOpen(true)} title="Ask about this meeting" aria-label="Ask about this meeting">
        <ChatIcon />
      </button>
    );
  }

  return (
    <section
      className={`chat-panel${large ? " large" : ""}`}
      aria-label="Ask about this meeting"
      onKeyDown={(event) => event.key === "Escape" && setOpen(false)}
    >
      <header className="chat-head">
        <strong>Ask about this meeting</strong>
        <div className="chat-head-actions">
          {messages.length > 0 && (
            <button className="chat-link" onClick={() => clear.mutate()} disabled={clear.isPending || !!pending}>
              New chat
            </button>
          )}
          <button className="icon-button" onClick={() => setLarge((was) => !was)} title={large ? "Smaller" : "Larger"}
                  aria-label={large ? "Smaller" : "Larger"}>
            {large ? "⤡" : "⤢"}
          </button>
          <button className="icon-button" onClick={() => setOpen(false)} title="Close" aria-label="Close">
            ✕
          </button>
        </div>
      </header>

      <div className="chat-body">
        {!ready ? (
          <p className="chat-note">Questions can be asked once the meeting has a transcript.</p>
        ) : history.isLoading ? (
          <p className="chat-note">Loading…</p>
        ) : (
          <>
            {messages.length === 0 && !pending && (
              <div className="chat-empty">
                <p className="chat-note">
                  Ask anything about this meeting. Answers come from its minutes and transcript, and only you see this
                  conversation.
                </p>
                <div className="chat-starters">
                  {STARTERS.map((starter) => (
                    <button key={starter} className="tab" onClick={() => send(starter)}>
                      {starter}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((message, i) => <Bubble key={i} message={message} />)}
            {pending && (
              <>
                <Bubble message={{ role: "user", content: pending.question }} />
                {pending.answer ? (
                  <Bubble message={{ role: "assistant", content: pending.answer }} />
                ) : (
                  <div className="chat-bubble assistant chat-thinking">Reading the meeting…</div>
                )}
              </>
            )}
            {(error || history.error || clear.error) && (
              <div className="error">{error ?? ((history.error ?? clear.error) as Error).message}</div>
            )}
          </>
        )}
        <div ref={bottom} />
      </div>

      <form
        className="chat-input"
        onSubmit={(event) => {
          event.preventDefault();
          send(draft);
        }}
      >
        <textarea
          ref={input}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends; Shift+Enter is a new line.
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              send(draft);
            }
          }}
          placeholder={ready ? "Ask a question…" : "No transcript yet"}
          rows={2}
          maxLength={4000}
          disabled={!ready}
        />
        <button className="tab on" type="submit" disabled={!ready || !draft.trim() || !!pending}>
          {pending ? "…" : "Send"}
        </button>
      </form>
    </section>
  );
}

function Bubble({ message }: { message: ChatMessage }) {
  if (message.role === "user") return <div className="chat-bubble user">{message.content}</div>;
  // The model's Markdown. react-markdown never renders raw HTML.
  return (
    <div className="chat-bubble assistant markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
    </div>
  );
}

function ChatIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
      <path d="M8.5 11h.01M12 11h.01M15.5 11h.01" />
    </svg>
  );
}
